/**
 * Image-level scale and measuring for the design estimate on the board
 * (eval/TAKEOFF_PLAN.md "Design estimate"): a drawing image carries its own
 * scale, so runs and areas drawn over it measure correctly however the image
 * is moved, resized or rotated.
 *
 *   image.customData.scale  = { v: 1, unit: 'ft' | 'm', units_per_px, natural: [w, h], label, method }
 *   image.customData.source = { kind: 'pdf', title, page, page_pt: [w, h] }   (when known)
 *
 * `units_per_px` is real units per NATURAL image pixel (the file's own pixel
 * grid), not per board unit. Every scene point is mapped back into that grid
 * through the element's position, size, rotation, flip and crop before it is
 * measured. backend/apps/takeoff/scale.py mirrors this maths for the server.
 */

export type LengthUnit = 'ft' | 'm';
export type MeasureDisplay = 'ftin' | 'ft' | 'm';

export interface ImageScale {
  v: 1;
  unit: LengthUnit;
  /** Real units (ft | m) per natural image pixel. */
  units_per_px: number;
  /** Natural image size the scale was set against [w, h]. */
  natural: [number, number];
  /** "1/8\" = 1'-0\"" or "24'-0\" calibrated". */
  label: string;
  method: 'calibrate' | 'preset';
  set_by?: string;
  set_at?: number;
}

export interface ImageSource {
  kind: 'pdf';
  title?: string;
  page?: number;
  /** PDF page size in points [w, h] (the full page the image was rendered from). */
  page_pt?: [number, number];
}

/** A dimension line: an arrow with bar (or arrow) ends and a bound length label; never a quantity. */
export interface DimensionData {
  v: 1;
  role: 'dimension';
  ends: 'bar' | 'arrow';
  placed_at: number;
}

export interface MeasureData {
  v: 1;
  role: 'measure';
  measure: 'linear' | 'area';
  placed_at: number;
}

/** The parts of an Excalidraw element this module reads. */
export interface GeomEl {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  angle?: number;
  isDeleted?: boolean;
  locked?: boolean;
  points?: readonly (readonly [number, number])[];
  scale?: readonly [number, number];
  crop?: { x: number; y: number; width: number; height: number; naturalWidth: number; naturalHeight: number } | null;
  customData?: Record<string, unknown> | null;
  polygon?: boolean;
  fileId?: string | null;
  /** Excalidraw: non-null on a curved line/arrow (drawn as a spline through its points). */
  roundness?: { type: number } | null;
  elbowed?: boolean;
}

/**
 * The points of the curve Excalidraw actually draws for a round line/arrow: rough.js stroke `curve()` with
 * curveTightness 0, i.e. Catmull-Rom segments as cubic Béziers (rough.js `_curveWithOffset`/`_curve`, with its
 * end padding), sampled `steps` times per segment. Straight lines come back unchanged.
 */
export function curvePoints(points: readonly (readonly [number, number])[], steps = 24): [number, number][] {
  const n = points.length;
  const plain = points.map((p) => [p[0], p[1]] as [number, number]);
  if (n < 3) return plain;
  // rough.js strokes (_curveWithOffset → _curve) pad the ends and pass through every point.
  const pts: (readonly [number, number])[] = [points[0], points[0], ...points.slice(1), points[n - 1]];
  const out: [number, number][] = [[pts[0][0], pts[0][1]]];
  for (let i = 1; i + 2 < pts.length; i++) {
    const start = out[out.length - 1];
    const b1: Pt = [pts[i][0] + (pts[i + 1][0] - pts[i - 1][0]) / 6, pts[i][1] + (pts[i + 1][1] - pts[i - 1][1]) / 6];
    const b2: Pt = [pts[i + 1][0] + (pts[i][0] - pts[i + 2][0]) / 6, pts[i + 1][1] + (pts[i][1] - pts[i + 2][1]) / 6];
    const b3: Pt = [pts[i + 1][0], pts[i + 1][1]];
    for (let k = 1; k <= steps; k++) {
      const t = k / steps, u = 1 - t;
      const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
      out.push([a * start[0] + b * b1[0] + c * b2[0] + d * b3[0], a * start[1] + b * b1[1] + c * b2[1] + d * b3[1]]);
    }
  }
  return out;
}

const isCurved = (el: GeomEl) => (el.type === 'line' || el.type === 'arrow') && !!el.roundness && !el.elbowed;

export type Pt = readonly [number, number];

// ---- lengths ------------------------------------------------------------------------------

const FT_PER_M = 1 / 0.3048;

/**
 * Parse a typed real-world length: 24'-0", 24' 6", 24'6", 24', 24 ft, 24.5ft,
 * 288", 288 in, 7.3 m, 730 cm, 7300 mm. A bare number is feet (or metres when
 * `bareUnit` is 'm'). Returns null for anything else or ≤ 0.
 */
export function parseLength(input: string, bareUnit: LengthUnit = 'ft'): { value: number; unit: LengthUnit } | null {
  const s = input.trim().toLowerCase().replace(/[′’]/g, "'").replace(/[″”“]/g, '"').replace(/\s+/g, ' ');
  if (!s) return null;
  const num = '(\\d+(?:\\.\\d+)?|\\.\\d+)';
  const frac = '(?:\\s*(\\d+)\\/(\\d+))?';
  let m = s.match(new RegExp(`^${num}\\s*(?:'|ft|feet|foot)\\s*-?\\s*(?:${num}${frac}\\s*(?:"|in|inch|inches)?)?$`));
  if (m) {
    const ft = Number(m[1]);
    const inch = (m[2] ? Number(m[2]) : 0) + (m[3] && m[4] ? Number(m[3]) / Number(m[4]) : 0);
    return positive(ft + inch / 12, 'ft');
  }
  m = s.match(new RegExp(`^${num}${frac}\\s*(?:"|in|inch|inches)$`));
  if (m) return positive((Number(m[1]) + (m[2] && m[3] ? Number(m[2]) / Number(m[3]) : 0)) / 12, 'ft');
  m = s.match(new RegExp(`^${num}\\s*(mm|cm|m|metres?|meters?)$`));
  if (m) {
    const f = m[2] === 'mm' ? 0.001 : m[2] === 'cm' ? 0.01 : 1;
    return positive(Number(m[1]) * f, 'm');
  }
  m = s.match(new RegExp(`^${num}$`));
  if (m) return positive(Number(m[1]), bareUnit);
  return null;
}

function positive(value: number, unit: LengthUnit) {
  return Number.isFinite(value) && value > 0 ? { value, unit } : null;
}

export function convertLength(value: number, from: LengthUnit, to: LengthUnit): number {
  if (from === to) return value;
  return from === 'm' ? value * FT_PER_M : value / FT_PER_M;
}

export function convertArea(value: number, from: LengthUnit, to: LengthUnit): number {
  if (from === to) return value;
  return from === 'm' ? value * FT_PER_M * FT_PER_M : value / (FT_PER_M * FT_PER_M);
}

/** Length in an item's UOM (LF | M); EACH etc. stay in feet. */
export function lengthInUom(value: number, unit: LengthUnit, uom: string): number {
  return convertLength(value, unit, uom === 'M' ? 'm' : 'ft');
}

export function areaInUom(value: number, unit: LengthUnit, uom: string): number {
  return convertArea(value, unit, uom === 'M2' ? 'm' : 'ft');
}

/** 24'-6", 24.5 ft or 7.47 m. */
export function formatLength(value: number, unit: LengthUnit, display: MeasureDisplay = 'ftin'): string {
  if (display === 'm') return `${round(convertLength(value, unit, 'm'), 2)} m`;
  const ft = convertLength(value, unit, 'ft');
  if (display === 'ft') return `${round(ft, 2)} ft`;
  let whole = Math.floor(ft + 1e-9);
  let inches = Math.round((ft - whole) * 12);
  if (inches === 12) {
    whole += 1;
    inches = 0;
  }
  return `${whole}'-${inches}"`;
}

export function formatArea(value: number, unit: LengthUnit, display: MeasureDisplay = 'ftin'): string {
  if (display === 'm') return `${round(convertArea(value, unit, 'm'), 2)} m²`;
  return `${round(convertArea(value, unit, 'ft'), 1)} sf`;
}

function round(x: number, d: number): string {
  const f = 10 ** d;
  const r = Math.round(x * f) / f;
  return Number.isInteger(r) ? String(r) : r.toFixed(d).replace(/0+$/, '').replace(/\.$/, '');
}

// ---- scale presets ------------------------------------------------------------------------

export interface ScalePreset {
  key: string;
  label: string;
  group: 'Architectural' | 'Engineering' | 'Metric';
  unit: LengthUnit;
  /** Real units per PDF point on the printed sheet. */
  unitsPerPt: number;
}

const ARCH: [string, number][] = [
  ['1/32"', 1 / 32], ['1/16"', 1 / 16], ['3/32"', 3 / 32], ['1/8"', 1 / 8], ['3/16"', 3 / 16], ['1/4"', 1 / 4],
  ['3/8"', 3 / 8], ['1/2"', 1 / 2], ['3/4"', 3 / 4], ['1"', 1], ['1 1/2"', 1.5], ['3"', 3],
];
const ENG = [10, 20, 30, 40, 50, 60, 100];
const METRIC = [10, 20, 25, 50, 75, 100, 125, 200, 250, 500, 1000];

export const SCALE_PRESETS: readonly ScalePreset[] = [
  // a" on paper = 1 ft → 1 pt (1/72") = 1 / (72 a) ft
  ...ARCH.map(([l, a]) => ({ key: `arch-${a}`, label: `${l} = 1'-0"`, group: 'Architectural' as const, unit: 'ft' as const, unitsPerPt: 1 / (72 * a) })),
  // 1" = N ft → 1 pt = N / 72 ft
  ...ENG.map((n) => ({ key: `eng-${n}`, label: `1" = ${n}'`, group: 'Engineering' as const, unit: 'ft' as const, unitsPerPt: n / 72 })),
  // 1:N → 1 pt (0.3528 mm) = 0.0254 N / 72 m
  ...METRIC.map((n) => ({ key: `metric-${n}`, label: `1:${n}`, group: 'Metric' as const, unit: 'm' as const, unitsPerPt: (0.0254 * n) / 72 })),
];

/** Scale from a printed-sheet preset; needs the source page size and the image's natural size. */
export function scaleFromPreset(p: ScalePreset, pagePt: readonly [number, number], natural: readonly [number, number]): ImageScale {
  const ptPerPx = pagePt[0] / natural[0];
  return { v: 1, unit: p.unit, units_per_px: p.unitsPerPt * ptPerPx, natural: [natural[0], natural[1]], label: p.label, method: 'preset' };
}

/** Scale from two natural-pixel points and the real distance between them. */
export function scaleFromCalibration(a: Pt, b: Pt, length: { value: number; unit: LengthUnit }, natural: readonly [number, number]): ImageScale | null {
  const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(d > 0)) return null;
  return {
    v: 1,
    unit: length.unit,
    units_per_px: length.value / d,
    natural: [natural[0], natural[1]],
    label: `${formatLength(length.value, length.unit, length.unit === 'm' ? 'm' : 'ftin')} calibrated`,
    method: 'calibrate',
  };
}

// ---- element geometry -----------------------------------------------------------------------

export function scaleOf(el: { customData?: Record<string, unknown> | null }): ImageScale | null {
  const s = (el.customData as { scale?: ImageScale } | null | undefined)?.scale;
  return s && s.v === 1 && s.units_per_px > 0 && Array.isArray(s.natural) && s.natural[0] > 0 && s.natural[1] > 0 ? s : null;
}

export function sourceOf(el: { customData?: Record<string, unknown> | null }): ImageSource | null {
  const s = (el.customData as { source?: ImageSource } | null | undefined)?.source;
  return s && s.kind === 'pdf' ? s : null;
}

export function measureOf(el: { customData?: Record<string, unknown> | null; isDeleted?: boolean }): MeasureData | null {
  if (el.isDeleted) return null;
  const m = (el.customData as { estimate?: MeasureData } | null | undefined)?.estimate;
  return m && m.role === 'measure' ? m : null;
}

export function dimensionOf(el: { customData?: Record<string, unknown> | null; isDeleted?: boolean }): DimensionData | null {
  if (el.isDeleted) return null;
  const d = (el.customData as { estimate?: DimensionData } | null | undefined)?.estimate;
  return d && d.role === 'dimension' ? d : null;
}

/** The label a dimension shows: its length on the scaled drawing under it, or "no scale". */
export function dimensionText(scene: readonly GeomEl[], el: GeomEl, display: MeasureDisplay = 'ftin'): string {
  const m = measureOnImage(scene, el, 'linear');
  return m ? formatLength(m.value, m.unit, display) + (m.spans ? ' ⚠' : '') : 'no scale';
}

function rotate([x, y]: Pt, [cx, cy]: Pt, angle: number): [number, number] {
  if (!angle) return [x, y];
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const dx = x - cx;
  const dy = y - cy;
  return [cx + dx * c - dy * s, cy + dx * s + dy * c];
}

/** Scene point → the image's natural pixel grid (position, size, rotation, flip, crop). */
export function sceneToNatural(img: GeomEl, p: Pt, natural: readonly [number, number]): [number, number] {
  const w = img.width || 1;
  const h = img.height || 1;
  const c: Pt = [img.x + w / 2, img.y + h / 2];
  const [ux, uy] = rotate(p, c, -(img.angle ?? 0));
  let lx = ux - img.x;
  let ly = uy - img.y;
  if (img.scale && img.scale[0] < 0) lx = w - lx;
  if (img.scale && img.scale[1] < 0) ly = h - ly;
  const crop = img.crop;
  if (crop && crop.width > 0 && crop.height > 0) {
    // Crop rectangles are in the file's pixels; rescale if the natural size differs.
    const kx = natural[0] / (crop.naturalWidth || natural[0]);
    const ky = natural[1] / (crop.naturalHeight || natural[1]);
    return [(crop.x + (lx * crop.width) / w) * kx, (crop.y + (ly * crop.height) / h) * ky];
  }
  return [(lx * natural[0]) / w, (ly * natural[1]) / h];
}

/** Is the scene point inside the (rotated) element box? */
export function containsPoint(el: GeomEl, p: Pt): boolean {
  const c: Pt = [el.x + el.width / 2, el.y + el.height / 2];
  const [ux, uy] = rotate(p, c, -(el.angle ?? 0));
  return ux >= el.x && ux <= el.x + el.width && uy >= el.y && uy <= el.y + el.height;
}

/**
 * Absolute scene points of a line / arrow (points are relative to x, y and
 * rotate about the centre of their bounds), or the outline of a rectangle /
 * diamond / ellipse.
 */
export function scenePoints(el: GeomEl): [number, number][] {
  const angle = el.angle ?? 0;
  if ((el.type === 'line' || el.type === 'arrow' || el.type === 'freedraw') && el.points?.length) {
    const verts = el.points.map(([px, py]) => [el.x + px, el.y + py] as [number, number]);
    const abs = isCurved(el) ? curvePoints(verts) : verts;
    if (!angle) return abs;
    const xs = verts.map((p) => p[0]);
    const ys = verts.map((p) => p[1]);
    const c: Pt = [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
    return abs.map((p) => rotate(p, c, angle));
  }
  const c: Pt = [el.x + el.width / 2, el.y + el.height / 2];
  let local: [number, number][];
  if (el.type === 'ellipse') {
    local = Array.from({ length: 64 }, (_, i) => {
      const t = (i / 64) * Math.PI * 2;
      return [c[0] + (el.width / 2) * Math.cos(t), c[1] + (el.height / 2) * Math.sin(t)] as [number, number];
    });
  } else if (el.type === 'diamond') {
    local = [
      [c[0], el.y],
      [el.x + el.width, c[1]],
      [c[0], el.y + el.height],
      [el.x, c[1]],
    ];
  } else {
    local = [
      [el.x, el.y],
      [el.x + el.width, el.y],
      [el.x + el.width, el.y + el.height],
      [el.x, el.y + el.height],
    ];
  }
  return angle ? local.map((p) => rotate(p, c, angle)) : local;
}

export function polylineLength(pts: readonly Pt[]): number {
  let L = 0;
  for (let i = 1; i < pts.length; i++) L += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  return L;
}

/** Shoelace area; the polygon closes itself. */
export function polygonArea(pts: readonly Pt[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

/** Vertices plus segment midpoints: what "most of the polyline" is judged on. */
function samples(pts: readonly Pt[]): Pt[] {
  const out: Pt[] = [...pts];
  for (let i = 1; i < pts.length; i++) out.push([(pts[i][0] + pts[i - 1][0]) / 2, (pts[i][1] + pts[i - 1][1]) / 2]);
  return out;
}

export interface ScaledHit {
  image: GeomEl;
  scale: ImageScale;
  /** Share of the measured points on this image (0–1). */
  share: number;
  /** Another scaled image holds a real part of the measurement too. */
  spans: GeomEl | null;
}

/**
 * The topmost scaled image holding most of the points (scene order: later =
 * on top). `spans` names a second scaled image that holds at least a quarter
 * of the points the winner doesn't — the measurement crosses two drawings.
 */
export function scaledImageUnder(scene: readonly GeomEl[], pts: readonly Pt[]): ScaledHit | null {
  const probe = samples(pts);
  if (!probe.length) return null;
  const hits: { image: GeomEl; scale: ImageScale; n: number; z: number; inside: boolean[] }[] = [];
  scene.forEach((el, z) => {
    if (el.isDeleted || el.type !== 'image') return;
    const scale = scaleOf(el);
    if (!scale) return;
    const inside = probe.map((p) => containsPoint(el, p));
    const n = inside.filter(Boolean).length;
    if (n) hits.push({ image: el, scale, n, z, inside });
  });
  if (!hits.length) return null;
  hits.sort((a, b) => b.n - a.n || b.z - a.z);
  const best = hits[0];
  const rest = probe.length - best.n;
  const other = hits.slice(1).find((h) => rest > 0 && h.inside.filter((v, i) => v && !best.inside[i]).length >= Math.max(1, rest * 0.25));
  return { image: best.image, scale: best.scale, share: best.n / probe.length, spans: other ? other.image : null };
}

export interface Measurement {
  kind: 'linear' | 'area';
  /** In `unit` (ft | m) — or ft² / m² for areas. */
  value: number;
  unit: LengthUnit;
  image: GeomEl;
  spans: GeomEl | null;
}

/** Is this element measured as an area (closed shape) rather than a run? */
export function isAreaShape(el: GeomEl): boolean {
  return el.type === 'rectangle' || el.type === 'ellipse' || el.type === 'diamond' || (el.type === 'line' && (!!el.polygon || isLoop(el)));
}

/** A line whose last point meets its first (Excalidraw draws and fills it as a closed shape). */
export function isLoop(el: GeomEl): boolean {
  const p = el.points;
  if (!p || p.length < 4) return false;
  const [a, b] = [p[0], p[p.length - 1]];
  return Math.hypot(a[0] - b[0], a[1] - b[1]) < 1e-6;
}

/** Measure a line (run) or closed shape (area) against the scaled image under it. */
export function measureOnImage(scene: readonly GeomEl[], el: GeomEl, kind: 'linear' | 'area' = isAreaShape(el) ? 'area' : 'linear'): Measurement | null {
  const pts = scenePoints(el);
  if (pts.length < 2) return null;
  const hit = scaledImageUnder(scene, pts);
  if (!hit) return null;
  const nat = pts.map((p) => sceneToNatural(hit.image, p, hit.scale.natural));
  const u = hit.scale.units_per_px;
  const value = kind === 'area' ? polygonArea(nat) * u * u : polylineLength(nat) * u;
  return { kind, value, unit: hit.scale.unit, image: hit.image, spans: hit.spans };
}

/** Viewport (CSS px) position of a scene point. */
export function toViewport(st: { scrollX: number; scrollY: number; zoom: { value: number } }, p: Pt): [number, number] {
  return [(p[0] + st.scrollX) * st.zoom.value, (p[1] + st.scrollY) * st.zoom.value];
}

/** The badge anchor of an image: its top-left corner after rotation. */
export function topLeft(el: GeomEl): [number, number] {
  return rotate([el.x, el.y], [el.x + el.width / 2, el.y + el.height / 2], el.angle ?? 0);
}

/** Midpoint along a polyline (for its length label). */
export function midpointAlong(pts: readonly Pt[]): [number, number] {
  if (pts.length === 1) return [pts[0][0], pts[0][1]];
  let half = polylineLength(pts) / 2;
  for (let i = 1; i < pts.length; i++) {
    const d = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    if (half <= d && d > 0) {
      const t = half / d;
      return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t];
    }
    half -= d;
  }
  return [pts[pts.length - 1][0], pts[pts.length - 1][1]];
}

export function centroid(pts: readonly Pt[]): [number, number] {
  let x = 0;
  let y = 0;
  for (const p of pts) {
    x += p[0];
    y += p[1];
  }
  return [x / pts.length, y / pts.length];
}
